import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import header from './components/header';
import accordion from './components/accordion';
import slider from './components/slider';
import form from './components/form';
import drawer from './components/drawer';
import { preloadImages } from './utils';

gsap.registerPlugin(ScrollTrigger);

window.addEventListener('DOMContentLoaded', () => {
    const lenis = new Lenis({
        lerp: 0.1,
        smooth: true,
    });

    window.lenis = lenis;

    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);

    header();
    accordion();
    slider();
    form();
    drawer();

    preloadImages('img').then(() => {
        lenis.resize();
    });

    const mm = gsap.matchMedia();
    mm.add('(min-width: 1024px)', () => {
        const blocks = [...document.querySelectorAll('.js-block')];

        if (blocks.length > 0) {
            const titles = [...document.querySelectorAll('.js-block-title')];

            const setActive = (title) => {
                title.classList.add('is-active');
                titles.filter((titleEle) => titleEle !== title).forEach((el) => el.classList.remove('is-active'));
            };

            blocks.forEach((block, index) => {
                gsap.from(block, {
                    scrollTrigger: {
                        trigger: block,
                        scrub: true,
                        start: `${block.dataset.startTop !== undefined ? block.dataset.startTop : '-110px'} top`,
                        onEnter: () => setActive(titles[index]),
                        onEnterBack: () => setActive(titles[index]),
                    },
                });

                const blockContentWrapper = block.querySelector('.js-block-content-wrapper');
                const blockContent = block.querySelector('.js-block-content');

                // Set initial z-index to create stacking order
                block.style.zIndex = blocks.length - index;

                gsap.to(blockContent, {
                    ease: 'power1.in',
                    scrollTrigger: {
                        pin: blockContentWrapper,
                        trigger: block,
                        start: 'top 110px',
                        end: index === blocks.length - 1 ? 'top 110px' : `bottom ${(blocks[blocks.length - 1].offsetHeight + 110)}px`,
                        endTrigger: index === blocks.length - 1 ? null : document.querySelector('.js-blocks'),
                        pinSpacing: false,
                    },
                });
            });
        }

        const panels = document.querySelectorAll('.js-panel');

        if (panels.length > 0) {
            panels.forEach((panel, index) => {
                const contentWrapper = panel.querySelector('.js-panel-content-wrapper');
                const content = panel.querySelector('.js-panel-content');

                // Set initial z-index to create stacking order
                panel.style.zIndex = panels.length - index;

                gsap.to(content, {
                    ease: 'power1.in',
                    scrollTrigger: {
                        pin: contentWrapper,
                        trigger: panel,
                        start: 'top 110px',
                        end: index === panels.length - 1 ? 'top 110px' : `+=${panel.offsetHeight}`,
                        pinSpacing: false,
                    },
                });

                if (index !== panels.length - 1) {
                    gsap.to(contentWrapper, {
                        opacity: 0.2,
                        scale: 0.98,
                        ease: 'power1.in',
                        scrollTrigger: {
                            trigger: content,
                            start: 'top 110px',
                            end: `+=${panel.offsetHeight / 2}`,
                            scrub: true,
                        },
                    });
                }
            });
        }
    });

    const waitForFontsLoaded = document.fonts?.ready.then(() => {
        if (document.fonts?.status === 'loaded') {
            return null;
        }
        console.warn('Browser reported fonts ready but something is still loading...');
        return new Promise((resolve) => {
            let waitTimeMs = 5;
            const checkFontsLoaded = () => {
                if (document.fonts?.status === 'loaded') {
                    return resolve();
                }
                waitTimeMs *= 2;
                return setTimeout(checkFontsLoaded, waitTimeMs);
            };
            setTimeout(checkFontsLoaded, 5);
        });
    });

    const sentence1 = document.querySelector('.js-sentence1');
    const sentence2 = document.querySelector('.js-sentence2');

    if (sentence1 && sentence2) {
        (async () => {
            await waitForFontsLoaded;

            gsap.to(sentence1, { // gsap.to() for forward movement
                x: -sentence1.clientWidth / 2, // Half the width of the phrase
                ease: 'none',
                duration: 20,
                repeat: -1,
            });
            gsap.from(sentence2, { // gsap.from() for backward movement
                x: -sentence2.clientWidth / 2, // Half the width of the phrase
                ease: 'none',
                duration: 20,
                repeat: -1,
            });
        })();

        gsap.to([sentence1, sentence2], {
            yPercent: '-=100',
            ease: 'power1.inOut',
            scrollTrigger: {
                trigger: sentence1,
                start: 'top 50%',
                end: 'bottom 75%',
                scrub: 1,
            },
        });
    }

    document.querySelectorAll('.js-word')?.forEach((word) => {
        gsap.to(word.children, {
            yPercent: '+=100',
            ease: 'expo.inOut',
            scrollTrigger: {
                trigger: word,
                start: 'bottom bottom',
                end: 'top 55%',
                scrub: 0.4, // Smooth scrubbing, takes 0.4 seconds to complete
            },
        });
    });
});
